import React, { useState, useEffect } from 'react';
import { Card, Spinner, Alert, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '../../api/apiClient';
import { useTranslation } from 'react-i18next';

export default function SelectCustomer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch customers with search
  const fetchCustomers = async (search = searchTerm) => {
    try {
      setLoading(true);
      setError('');
      const response = await usersAPI.index({ search });
      
      if (response.success) {
        const customersData = response.data.data || response.data.data?.data || [];
        
        // Filter out invalid entries if any
        const validCustomers = customersData.filter(customer => 
          customer && (customer.firstname || customer.email || customer.phone)
        );
        
        setCustomers(validCustomers);
      } else {
        setError(response.message || t('failed_to_fetch_customers'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_customers'));
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchCustomers(searchTerm);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  // دالة للحصول على الحرف الأول من الاسم بشكل آمن
  const getInitial = (customer) => {
    if (customer.firstname) {
      return customer.firstname.charAt(0).toUpperCase();
    } else if (customer.email) {
      return customer.email.charAt(0).toUpperCase();
    } else if (customer.phone) {
      return customer.phone.charAt(0);
    }
    return '?';
  };

  // دالة للحصول على الاسم المعروض بشكل آمن
  const getDisplayName = (customer) => {
    if (customer.firstname) {
      return customer.firstname;
    } else if (customer.email) {
      return customer.email;
    } else if (customer.phone) {
      return customer.phone;
    }
    return t('unknown_customer');
  };

  // دالة للحصول على التفاصيل المعروضة بشكل آمن
  const getDisplayDetails = (customer) => {
    const details = [];
    if (customer.email) details.push(customer.email);
    if (customer.phone) details.push(customer.phone);
    return details.join(' • ') || t('no_contact_info');
  };

  const startChat = (customer) => {
    navigate(`/admin/client-list/chat-client/${customer.id}`, {
      state: { newConversation: true }
    });
  };

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
        <h4 className="fw-bold mb-0">👥 {t('select_customer')}</h4>
        <Button className='add-btn btn btn-primary' onClick={() => navigate('/admin/client-list')}>
          {t('back_to_conversations')}
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Search Bar */}
      <Form.Group className="mb-4">
        <Form.Control
          type="text"
          placeholder={t('search_customers_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="rounded-pill"
        />
        <Form.Text className="text-muted">
          {t('search_by_name_email_phone')}
        </Form.Text>
      </Form.Group>

      {customers.length === 0 ? (
        <Alert variant="info">
          {searchTerm ? t('no_customers_found') : t('no_customers_available')}
        </Alert>
      ) : (
        <Row>
          {customers.map(customer => (
            <Col md={6} lg={4} key={customer.id} className="mb-3">
              <Card className="h-100 shadow-sm customer-card">
                <Card.Body className="text-center">
                  <div className="customer-avatar mb-3">
                    <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center"
                         style={{ width: '60px', height: '60px', fontSize: '24px' }}>
                      {getInitial(customer)}
                    </div>
                  </div>
                  
                  <h6 className="fw-bold mb-1">{getDisplayName(customer)}</h6>
                  <p className="text-muted small mb-2">{getDisplayDetails(customer)}</p>
                  
                  {customer.role_name && (
                    <Badge bg="secondary" className="mb-2">
                      {customer.role_name}
                    </Badge>
                  )}
                  
                  <div className="mt-3">
                    <Button
                      variant="success"
                      onClick={() => startChat(customer)}
                      className="rounded-pill px-4"
                    >
                      {t('start_chat')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <style>{`
        .customer-card {
          transition: transform 0.2s;
          border: 1px solid #e9ecef;
        }
        
        .customer-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          border-color: #28a745;
        }
      `}</style>
    </div>
  );
}